import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {User} from '../../user/user';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {DbServerErrors} from "../../shared/interceptors/db-server-errors";
import {DbResponse} from "../../shared/interceptors/db-response";
import {NotifierComponent} from "../../shared/components/notifier/notifier.component";
import {Notification} from "../../shared/components/notifier/notification";
import {NotificationType} from "../../shared/components/notifier/notification-type";
import timezones from "../../../assets/json/timezones.json";
import {ActivatedRoute} from '@angular/router';
import {IFcDropdownSelectOption} from "../../shared/components/form-controls/dropdown-select/fc-dropdown-select-option";
import {UserService} from '../../user/user.service';
import * as fromStore from "../../store";
import {Store} from '@ngrx/store';
import {filter, first, switchMap, takeUntil} from 'rxjs/operators';
import {DialogService} from "../../../ng2-bootstrap-modal/dialog.service";
import {Utils} from "../../shared/utils/utils";
import {Subject} from "rxjs";


@Component({
    selector: 'db-account-settings',
    templateUrl: './account-settings.component.html',
    styleUrls: ['./account-settings.component.scss'],
    standalone: false
})
export class AccountSettingsComponent implements OnInit, OnDestroy {

  @Input() isMobile: boolean;

  public timeZones = timezones;
  public loading: boolean;
  public timeZoneOptions: IFcDropdownSelectOption[];

  private _user: User;
  public accountSettings: FormGroup;
  public serverErrors: DbServerErrors;
  public hasServerFieldErrors: boolean;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
    private _userService: UserService,
    private _dialogService: DialogService
  ) {
  }

  ngOnInit() {
    this._user = this._route.snapshot.data['user'];
    this.accountSettings = new FormGroup({
      'firstName': new FormControl('', [
        Validators.required,
        Utils.patternValidator(/[-a-zA-Z0-9@:%_+.~#?&\/=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&\/=]*)?/, {link: true}, true)
      ]),
      'lastName': new FormControl('', [
        Validators.required,
        Utils.patternValidator(/[-a-zA-Z0-9@:%_+.~#?&\/=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&\/=]*)?/, {link: true}, true)
      ]),
      'email': new FormControl('', [Validators.required]),
      'phoneNumber': new FormControl('', [Validators.pattern(/^(\+|)[0-9 ]+$/)]),
      'timeZone': new FormControl('UTC'),
      'companyWebsite': new FormControl('', [Validators.pattern('https?:\/\/.+')])
    });

    const timezonesFormatted = [];
    Object.keys(this.timeZones).forEach((key: string): void => {
      timezonesFormatted.push({
        "label": this.timeZones[key],
        "value": this.timeZones[key]
      });
    });
    this.timeZoneOptions = timezonesFormatted;
    this.refreshFormData();
    this.addEventHandlers();
  }

  private addEventHandlers(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUser)),
        filter((user: User) => !!user),
        takeUntil(this._destroyStream$)
      )
      .subscribe((user: User) => {
        this._user = user;
        this.refreshFormData();
      });
  }

  ngOnDestroy(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private refreshFormData(): void {
    const user: User = this._user;
    this.accountSettings.setValue({
      'firstName': this.decodeEntities(user.firstName),
      'lastName': this.decodeEntities(user.lastName),
      'email': user.email,
      'phoneNumber': user.phoneNumber,
      'timeZone': user.timeZone ? user.timeZone : 'UTC',
      'companyWebsite': user.companyWebsite,
    }, {emitEvent: false});
  }

  public decodeEntities(encodedString) {
    const translate_re = /&(nbsp|amp|quot|lt|gt);/g;
    const translate = {
      "nbsp": " ",
      "amp": "&",
      "quot": "\"",
      "lt": "<",
      "gt": ">"
    };
    return encodedString.replace(translate_re, function (match, entity) {
      return translate[entity];
    }).replace(/&#(\d+);/gi, function (match, numStr) {
      const num = parseInt(numStr, 10);
      return String.fromCharCode(num);
    });
  }

  public onAccountUpdate(): void {
    this.serverErrors = null;
    if (this.accountSettings.valid && this.accountSettings.controls['firstName'].value !== "" && this.accountSettings.controls['lastName'].value !== "") {
      this.loading = true;
      this._userService.updateUserData(this.accountSettings.value)
        .pipe(
          filter((response: DbResponse): boolean => !!response),
          first())
        .subscribe((response: DbResponse): void => {
          this.loading = false;

          if (response.success) {
            NotifierComponent.notify(new Notification(response.message, NotificationType.SUCCESS));
            this._store.dispatch(new fromStore.LoadUser);
          } else {
            this.serverErrors = response.serverErrors;
            this.hasServerFieldErrors = !!Utils.intersect(Object.keys(this.serverErrors.fields), ['name', 'first_name', 'last_name', 'email', 'password', 'stripe_token']).length;
            if (!this.hasServerFieldErrors) {
              NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
            }
          }
        });
    }
  }
}
